import { Button, Card } from '@/core/ui/components';
import { AlertCircle, Loader2, RefreshCw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useAccessLevels } from '../../hooks/useAccessLevels';

interface AccessLevelSelectorProps {
  tenantId: string;
}

export function AccessLevelSelector({ tenantId }: AccessLevelSelectorProps) {
  const { accessLevels, loading, updating, error, refreshedAt, integrationId, refresh, setDefault, reload } =
    useAccessLevels(tenantId);

  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (refreshedAt === null) return;
    const remaining = Math.ceil((60_000 - (Date.now() - refreshedAt)) / 1000);
    if (remaining <= 0) return;
    setCooldownSeconds(remaining);
    intervalRef.current = setInterval(() => {
      setCooldownSeconds(prev => {
        if (prev <= 1) {
          clearInterval(intervalRef.current!);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(intervalRef.current!);
  }, [refreshedAt]);

  const cooldownActive = cooldownSeconds > 0;

  const visitors = accessLevels.filter(al => al.type === 'visitor');
  const employees = accessLevels.filter(al => al.type === 'employee');

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-theme">
          Nivel de acceso predeterminado (HikCentral)
        </h3>
        <Button
          variant="secondary"
          leftIcon={<RefreshCw className="w-4 h-4" />}
          disabled={loading || updating || cooldownActive || !integrationId}
          onClick={refresh}
          size="sm"
        >
          {cooldownActive ? `Refrescar (${cooldownSeconds}s)` : 'Refrescar'}
        </Button>
      </div>

      {loading && (
        <div className="flex justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
        </div>
      )}

      {!loading && error && (
        <div className="flex items-start gap-2 p-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>{error}</span>
          <button onClick={reload} className="ml-auto text-red-600 underline text-xs hover:no-underline">
            Reintentar
          </button>
        </div>
      )}

      {!loading && !error && !integrationId && (
        <p className="text-sm text-theme-secondary py-4 text-center">
          No se encontró la integración HikCentral para este tenant.
        </p>
      )}

      {!loading && !error && integrationId && accessLevels.length === 0 && (
        <div className="text-center py-6">
          <p className="text-sm text-theme-secondary mb-3">
            No hay niveles de acceso configurados en HikCentral.
          </p>
          <Button variant="secondary" leftIcon={<RefreshCw className="w-4 h-4" />} onClick={refresh} size="sm">
            Refrescar
          </Button>
        </div>
      )}

      {!loading && !error && integrationId && accessLevels.length > 0 && (
        <div className="space-y-4">
          {visitors.length > 0 && (
            <AccessLevelGroup
              title="Visitantes"
              items={visitors}
              updating={updating}
              onSelect={setDefault}
            />
          )}
          {employees.length > 0 && (
            <AccessLevelGroup
              title="Empleados"
              items={employees}
              updating={updating}
              onSelect={setDefault}
            />
          )}
        </div>
      )}

      <p className="text-xs text-theme-secondary mt-3">
        El nivel seleccionado se asignará automáticamente a todos los visitantes creados por residentes de este tenant.
      </p>
    </Card>
  );
}

interface AccessLevelGroupProps {
  title: string;
  items: Array<{ id: string; name: string; isDefault: boolean }>;
  updating: boolean;
  onSelect: (id: string) => Promise<boolean>;
}

function AccessLevelGroup({ title, items, updating, onSelect }: AccessLevelGroupProps) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-theme-secondary mb-2">{title}</p>
      <div className="space-y-1">
        {items.map(item => (
          <label
            key={item.id}
            className={`flex items-center gap-3 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
              item.isDefault
                ? 'border-indigo-300 bg-indigo-50'
                : 'border-transparent hover:bg-gray-50'
            }`}
          >
            <input
              type="radio"
              name="default-access-level"
              value={item.id}
              checked={item.isDefault}
              disabled={updating}
              onChange={() => onSelect(item.id)}
              className="text-indigo-600 focus:ring-indigo-500"
            />
            <span className="text-sm text-theme flex-1">{item.name}</span>
            {item.isDefault && (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                Predeterminado
              </span>
            )}
          </label>
        ))}
      </div>
    </div>
  );
}
